import React from 'react';
import { TrendingUp, TrendingDown } from 'lucide-react';

export interface Stat {
  label: string;
  value: React.ReactNode;
  icon?: React.ReactNode;
  trend?: {
    direction: 'up' | 'down' | 'flat';
    label: string;
  };
  tone?: 'default' | 'income' | 'expense' | 'net';
}

interface Props {
  stats: Stat[];
  className?: string;
}

const toneClasses: Record<NonNullable<Stat['tone']>, string> = {
  default: 'text-slate-900',
  income:  'text-emerald-700',
  expense: 'text-rose-700',
  net:     'text-brand-700',
};

const StatStrip: React.FC<Props> = ({ stats, className = '' }) => {
  const cols =
    stats.length >= 4 ? 'sm:grid-cols-2 lg:grid-cols-4'
    : stats.length === 3 ? 'sm:grid-cols-3'
    : stats.length === 2 ? 'sm:grid-cols-2'
    : 'sm:grid-cols-1';

  return (
    <div className={`grid grid-cols-1 gap-3 ${cols} ${className}`}>
      {stats.map((s) => (
        <div
          key={s.label}
          className="rounded-2xl border border-slate-200 bg-white p-5 shadow-card"
        >
          <div className="flex items-center justify-between">
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">{s.label}</p>
            {s.icon && (
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-brand-50 text-brand-600">
                {s.icon}
              </div>
            )}
          </div>
          <p
            className={`mt-3 font-display text-[1.75rem] font-medium leading-none tracking-tight tabular-nums ${
              toneClasses[s.tone ?? 'default']
            }`}
          >
            {s.value}
          </p>
          {s.trend && (
            <div className="mt-2 flex items-center gap-1 text-xs font-semibold">
              {s.trend.direction === 'up' && (
                <TrendingUp className="h-3.5 w-3.5 text-emerald-600" aria-hidden />
              )}
              {s.trend.direction === 'down' && (
                <TrendingDown className="h-3.5 w-3.5 text-rose-600" aria-hidden />
              )}
              <span
                className={
                  s.trend.direction === 'up' ? 'text-emerald-700'
                  : s.trend.direction === 'down' ? 'text-rose-700'
                  : 'text-slate-600'
                }
              >
                {s.trend.label}
              </span>
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

export default StatStrip;
